/* @jsx h */
import {
  computed,
  defineComponent,
  h,
  inject,
  provide,
  ref,
  type ComputedRef,
  type PropType,
  type Ref,
} from "vue";
import { api, instrumentation } from "@app/data/index";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { IdentitySession, IdentitySessionShop } from "./Auth";

type GroupAuthContext = {
  session: Ref<IdentitySession>;
  identity: ComputedRef<IdentitySession["identity"]>;
  group: ComputedRef<IdentitySession["group"]>;
  shops: ComputedRef<IdentitySessionShop[]>;
  isGroupAdmin: ComputedRef<boolean>;
  exitToShop: (userId: number) => Promise<void>;
  logout: () => Promise<void>;
};

const GroupAuthContextKey = Symbol("GroupAuthContext");

export const GroupAuthProvider = defineComponent({
  name: "GroupAuthProvider",
  props: {
    session: {
      type: Object as PropType<IdentitySession>,
      required: true,
    },
  },
  setup(props, { slots }) {
    const trpc = useTrpc();
    const session = ref(props.session) as Ref<IdentitySession>;
    const exiting = ref(false);

    const exitToShop = async (userId: number) => {
      if (exiting.value) return;
      exiting.value = true;
      try {
        await trpc.session.exitGroupMode.mutate({ userId });
        window.location.assign("/app/pos");
      } catch (error) {
        exiting.value = false;
        throw error;
      }
    };

    const logout = async () => {
      await api.destroySession();
      instrumentation.reset();
      window.location.assign("/");
    };

    provide<GroupAuthContext>(GroupAuthContextKey, {
      session,
      identity: computed(() => session.value.identity),
      group: computed(() => session.value.group),
      shops: computed(() => session.value.shops),
      isGroupAdmin: computed(() => session.value.group_role === "admin"),
      exitToShop,
      logout,
    });

    return () => slots.default?.();
  },
});

export function useGroupAuth(): GroupAuthContext {
  const context = inject<GroupAuthContext>(GroupAuthContextKey);
  if (!context) {
    throw new Error("useGroupAuth must be used within GroupAuthProvider");
  }
  return context;
}
